import { Storage } from '@iheartjane/utils';
import { useEffect, useState } from 'react';

/**
 * A localStorage hook that automatically synchronizes between multiple components.
 *
 * Uses custom events to notify other hook instances when values change, eliminating
 * the need for prop drilling or context. Each key gets its own event channel for
 * optimal performance.
 * @param key - The localStorage key to store/retrieve the value
 * @param initialValue - The default value to use if no stored value exists
 * @example
 * const [sort, setSort] = useLocalStorage('selectedSort', 'proximity');
 * // Changes in one component automatically update all other components using the same key
 */

export const useLocalStorage = <T>(
  key: string,
  initialValue: T
): [T, (value: T | ((val: T) => T)) => void] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = Storage.get<T>(key);
      return item ? item : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  useEffect(() => {
    const handleStorageChange = (e: CustomEvent) => {
      setStoredValue(e.detail.value);
    };

    const eventName = `localStorage-change-${key}`;
    window.addEventListener(eventName, handleStorageChange as EventListener);

    return () => {
      window.removeEventListener(
        eventName,
        handleStorageChange as EventListener
      );
    };
  }, [key]);

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      Storage.set(key, valueToStore);

      window.dispatchEvent(
        new CustomEvent(`localStorage-change-${key}`, {
          detail: { value: valueToStore },
        })
      );
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
};
